import { useCallback, useEffect, useRef, useState } from 'react'

import {
  MarkAsSpamContainer,
  MarkAsSafeContainer,
} from '@chat/Dialog/MessageItem/ContextMenu'
import { SpamReportContainer } from '@chat/Dialog/MessageItem/SpamReport'
import { useTranslation } from 'react-i18next'

import { isMobileDevice } from '@app/helpers'
import { useIsStaffWithoutStaffAdmin, useModal } from '@app/hooks'

import { copyToClipboard } from '@app/utils/dom'

import { Icon, Modal } from '@lib/components'

import { IconStyles } from '@lib/components/Icon/styles'

import { ContextMenuItems } from './ContextMenuItems'

import styles from './styles.module.scss'
import { ContextMenuProps } from './types'

export const ContextMenu = ({
  conversationId,
  message,
  openMenuId,
  setOpenMenuId,
  abortController,
}: ContextMenuProps) => {
  const menuRef = useRef<HTMLDivElement>(null)
  const actionsRef = useRef<HTMLDivElement>(null)
  const [openAbove, setOpenAbove] = useState(false)
  const spamReportModal = useModal(`SpamReportModal_${message.id}`)
  const markAsSafeModal = useModal(`MarkAsSafeModal_${message.id}`)
  const markAsSpamModal = useModal(`MarkAsSpamModal_${message.id}`)
  const messageId = message.id.toString()
  const isOpen = openMenuId === messageId
  const { t } = useTranslation()
  const isStaffWithoutStaffAdmin = useIsStaffWithoutStaffAdmin()

  const calculateDirection = () => {
    if (actionsRef.current) {
      const actionsRect = actionsRef.current.getBoundingClientRect()
      const spaceAbove = actionsRect.top
      const spaceBelow = window.innerHeight - actionsRect.bottom
      setOpenAbove(spaceBelow < 270 && spaceAbove > spaceBelow)
    }
  }

  const toggleMenu = () => {
    setOpenMenuId?.(isOpen ? null : messageId)
    calculateDirection()
  }

  const closeModals = useCallback(() => {
    setOpenMenuId?.(null)
    spamReportModal.close()
    markAsSafeModal.close()
    markAsSpamModal.close()
  }, [])

  useEffect(() => {
    if (openMenuId === messageId) {
      calculateDirection()
    }
  }, [openMenuId, messageId])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        !isMobileDevice() &&
        menuRef.current &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setOpenMenuId?.(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [setOpenMenuId])

  const positionStyle = 'calc(100% + 5px)'

  const copyMessageID = useCallback(async () => {
    try {
      await copyToClipboard(message.id.toString())
    } catch (err) {
      console.error('Copy failed', err)
    } finally {
      setOpenMenuId?.(null)
    }
  }, [message.id, setOpenMenuId])

  return (
    <>
      <div className={styles.actions} ref={actionsRef}>
        <div className={styles.more} onClick={toggleMenu}>
          <Icon path="icons/icon-more" style={IconStyles.small} />
        </div>

        {isOpen && (
          <div
            onClick={toggleMenu}
            ref={menuRef}
            className={styles.menu}
            style={{
              top: openAbove ? undefined : positionStyle,
              bottom: openAbove ? positionStyle : undefined,
            }}
          >
            <ContextMenuItems
              isSafe={message.state != 'Spam'}
              canMarkAsSpamOrSafe={!isStaffWithoutStaffAdmin}
              onMarkAsSafe={markAsSafeModal.open}
              onMarkAsSpam={markAsSpamModal.open}
              onShowSpamReport={spamReportModal.open}
              onCopyMessageID={copyMessageID}
            />
          </div>
        )}
      </div>

      <Modal
        isOpen={isOpen}
        mobileOnly={true}
        onClose={closeModals}
        title={t('Chat.Message.ContextMenu.Actions')}
        className={styles.mobileMenu}
        headerClassName={styles.mobileMenuHeader}
        showBottomCloseButton={false}
      >
        <ContextMenuItems
          isSafe={message.state != 'Spam'}
          canMarkAsSpamOrSafe={!isStaffWithoutStaffAdmin}
          onMarkAsSafe={markAsSafeModal.open}
          onMarkAsSpam={markAsSpamModal.open}
          onShowSpamReport={spamReportModal.open}
          onCopyMessageID={copyMessageID}
        />
      </Modal>

      <Modal
        isOpen={spamReportModal.isOpen}
        onClose={closeModals}
        title={t('Chat.Message.SpamReport')}
        className={styles.spamModal}
        headerClassName={styles.mobileMenuHeader}
        showBottomCloseButton={false}
      >
        <SpamReportContainer message={message} />
      </Modal>
      <Modal
        isOpen={markAsSafeModal.isOpen}
        onClose={closeModals}
        title={t('Chat.Message.MarkAsSafe')}
        className={styles.markAsSafeOrSpamModal}
        headerClassName={styles.mobileMenuHeader}
        showBottomCloseButton={false}
      >
        <MarkAsSafeContainer
          conversationId={conversationId}
          message={message}
          onClose={closeModals}
          abortController={abortController}
        />
      </Modal>
      <Modal
        isOpen={markAsSpamModal.isOpen}
        onClose={closeModals}
        title={t('Chat.Message.MarkAsSpam')}
        className={styles.markAsSafeOrSpamModal}
        headerClassName={styles.mobileMenuHeader}
        showBottomCloseButton={false}
      >
        <MarkAsSpamContainer
          conversationId={conversationId}
          message={message}
          onClose={closeModals}
          abortController={abortController}
        />
      </Modal>
    </>
  )
}
